import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { Check, ChevronDown } from 'lucide-react';

export type StatusPillTone =
  | 'success'
  | 'warning'
  | 'info'
  | 'danger'
  | 'neutral'
  | 'outline';

export type StatusPillSize = 'sm' | 'md';

// Light theme uses the bold `.es-*` palette from globals.css (solid teal /
// amber / sea-foam / brick-red on cream text) — same treatment already used
// on the employee profile hero card so badges read clearly on a light
// background instead of the previous near-invisible emerald-on-emerald.
// `.dark .es-*` selectors in globals.css restore the soft tinted look for
// dark theme, so this is a pure light-mode contrast bump.
const TONE_CLASS: Record<StatusPillTone, string> = {
  success: 'es-active border border-transparent',
  warning: 'es-probation border border-transparent',
  info: 'es-onboarding border border-transparent',
  danger: 'es-terminated border border-transparent',
  neutral: 'bg-muted text-muted-foreground border border-border',
  outline: 'bg-transparent text-muted-foreground border border-border',
};

const SIZE_CLASS: Record<StatusPillSize, string> = {
  sm: 'inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-normal',
  md: 'inline-flex items-center gap-1.5 rounded-md px-2.5 py-0.5 text-xs font-medium',
};

export interface StatusPillOption<T extends string> {
  value: T;
  label: string;
  disabled?: boolean;
  disabledReason?: string;
}

interface StatusPillProps {
  tone: StatusPillTone;
  label: string;
  size?: StatusPillSize;
  className?: string;
}

export function StatusPill({ tone, label, size = 'md', className }: StatusPillProps) {
  return <span className={cn(SIZE_CLASS[size], TONE_CLASS[tone], className)}>{label}</span>;
}

interface StatusPillSelectProps<T extends string> {
  value: T;
  label: string;
  tone: StatusPillTone;
  options: Array<StatusPillOption<T>>;
  onChange: (value: T) => void;
  size?: StatusPillSize;
  align?: 'start' | 'end';
  disabled?: boolean;
  ariaLabel?: string;
  menuClassName?: string;
}

export function StatusPillSelect<T extends string>({
  value,
  label,
  tone,
  options,
  onChange,
  size = 'md',
  align = 'start',
  disabled = false,
  ariaLabel,
  menuClassName = 'w-44',
}: StatusPillSelectProps<T>) {
  return (
    <TooltipProvider delayDuration={200}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild disabled={disabled}>
          <button
            type="button"
            aria-label={ariaLabel}
            className={cn(
              SIZE_CLASS[size],
              TONE_CLASS[tone],
              'cursor-pointer transition-shadow hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50',
              disabled && 'cursor-not-allowed opacity-60',
            )}
          >
            {label}
            <ChevronDown className="h-3 w-3 shrink-0" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align={align} className={menuClassName}>
          {options.map((option) => {
            const isCurrent = option.value === value;
            const isDisabled = option.disabled === true || isCurrent;

            const item = (
              <DropdownMenuItem
                disabled={isDisabled}
                onSelect={(e) => {
                  if (isDisabled) {
                    e.preventDefault();
                    return;
                  }
                  onChange(option.value);
                }}
                className="flex items-center justify-between gap-2"
              >
                <span>{option.label}</span>
                {isCurrent && <Check className="h-3.5 w-3.5 text-muted-foreground" />}
              </DropdownMenuItem>
            );

            if (option.disabled && option.disabledReason && !isCurrent) {
              return (
                <Tooltip key={option.value}>
                  <TooltipTrigger asChild>
                    <div>{item}</div>
                  </TooltipTrigger>
                  <TooltipContent side="right">{option.disabledReason}</TooltipContent>
                </Tooltip>
              );
            }
            return <div key={option.value}>{item}</div>;
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    </TooltipProvider>
  );
}
